import { ConstantValues } from "common/constants";
import { useCallback, useEffect, useRef, useState } from "react";

interface IUseAdminListFiltersConfig<K extends string> {
  cacheKey: string;
  filterKeys: readonly K[];
}

type FilterState<K extends string> = Record<K, string>;

interface IUseAdminListFiltersReturn<K extends string> {
  filters: FilterState<K>;
  setFilter: (key: K, value: string) => void;
  resetFilters: () => void;
}

function parseFilterOptions(): Record<string, Record<string, string>> {
  try {
    const raw = localStorage.getItem(ConstantValues.FILTER_OPTIONS_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeFilterOptions(
  scope: string,
  filters: Record<string, string>,
): void {
  try {
    const parsed = parseFilterOptions();
    parsed[scope] = filters;
    localStorage.setItem(
      ConstantValues.FILTER_OPTIONS_CACHE_KEY,
      JSON.stringify(parsed),
    );
  } catch {
    // ignore
  }
}

function clearFilterOptions(scope: string): void {
  try {
    const parsed = parseFilterOptions();
    if (parsed[scope]) {
      delete parsed[scope];
      localStorage.setItem(
        ConstantValues.FILTER_OPTIONS_CACHE_KEY,
        JSON.stringify(parsed),
      );
    }
  } catch {
    // ignore
  }
}

export function useAdminListFilters<K extends string>(
  config: IUseAdminListFiltersConfig<K>,
): IUseAdminListFiltersReturn<K> {
  const { cacheKey, filterKeys } = config;

  // Stabilise the filterKeys reference so an inline array passed by the
  // caller does not cause the persistence effect to run on every render.
  const filterKeysRef = useRef(filterKeys);
  filterKeysRef.current = filterKeys;

  const [filters, setFilters] = useState<FilterState<K>>(() => {
    const saved = parseFilterOptions()[cacheKey];
    const initial = {} as FilterState<K>;
    for (const key of filterKeysRef.current) {
      initial[key] = saved?.[key] ?? "";
    }
    return initial;
  });

  useEffect(() => {
    const nonEmpty: Record<string, string> = {};
    let hasValue = false;
    for (const key of filterKeysRef.current) {
      if (filters[key]) {
        nonEmpty[key] = filters[key];
        hasValue = true;
      }
    }

    if (hasValue) {
      writeFilterOptions(cacheKey, nonEmpty);
    } else {
      clearFilterOptions(cacheKey);
    }
  }, [cacheKey, filters]);

  const setFilter = useCallback((key: K, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    const empty = {} as FilterState<K>;
    for (const key of filterKeysRef.current) {
      empty[key] = "";
    }
    setFilters(empty);
  }, []);

  return { filters, setFilter, resetFilters };
}
